import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  CREATE_CUSTOM_TIP_MENU_ITEM,
  GET_CUSTOM_TIP_MENU,
  PREVIEW_CUSTOM_TIP_MENU_ITEM,
  SANDBOX_DEPLOY_MENU,
  UPDATE_CUSTOM_TIP_MENU_ITEM,
} from "../../../queries/customTipMenuQueries";
import {
  ALLOWED_UPLOAD_TYPES,
  getImageUrlWarning,
  uploadMenuItemImage,
} from "../../../util/menuItemImage";
import {
  MAX_MENU_ITEM_TYPE_LENGTH,
  RESERVED_MENU_ITEM_TYPES,
  slashCommandForType,
  slugifyMenuItemType,
} from "../../../util/menuItemType";
import { supportsPrivateRedeem } from "../../../util/privateRedeem";
import { isSubscribableType } from "../../../util/subscriptionEligibility";
import { useProtectedMenuImages } from "../../../util/useProtectedMenuImages";
import Loading from "../../Loading";

interface CustomTipMenuItem {
  name: string;
  description: string;
  cost: number;
  friendlyName: string;
  type: string;
  command: string;
  title: string;
  footer: string;
  image: string;
  tipNote: string;
  color: string;
  isActive: boolean;
  isPrivate: boolean;
  isSubscribable: boolean;
  subscriptionWeeklyPrice: number | null;
  subscriptionMonthlyPrice: number | null;
  tags: string[];
}

interface CustomTipMenu {
  id: string;
  broadcaster: string;
  menuItems: CustomTipMenuItem[];
}

// Tag configuration with icons and descriptions
const TAG_CONFIG = {
  media_included: {
    icon: "💜",
    label: "Media included",
    description: "A pic or video will be sent (length/format may vary)",
  },
  fun_support: {
    icon: "🩷",
    label: "Fun/support",
    description:
      "Fun tip to engage or show support. Media may be sent sometimes, but isn't guaranteed",
  },
  photo: {
    icon: "📷",
    label: "Photo",
    description: "A picture will be sent",
  },
  video: {
    icon: "🎥",
    label: "Video",
    description:
      "A video will be sent (usually a short clip, occasional full videos)",
  },
  private_vip: {
    icon: "🔓",
    label: "Private/VIP",
    description: "Media sent via DM or VIP channels",
  },
  public: {
    icon: "🌟",
    label: "Public",
    description: "Media posted in the server",
  },
};

const EMPTY_CREATE_FORM = {
  name: "",
  friendlyName: "",
  // A string so the field can start empty rather than at 0.
  cost: "",
  description: "",
  title: "",
  footer: "",
  tipNote: "",
  image: "",
  color: "",
  isPrivate: false,
  tags: [] as string[],
};

const CREATE_IMAGE_HINT =
  "Use a permanent image link (e.g. Imgur) instead.";

const inputClass =
  "w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500";
const labelClass = "block text-sm font-semibold text-base-content mb-2";

// Used for both the create and edit modals - a broadcaster can upload a
// file (goes to S3, see /api/upload-menu-item-image) or paste a url, same
// as before. `type` only names the S3 object; it doesn't have to be final
// yet; the field a broadcaster is editing at save time is what's stored.
const ImageField = ({
  value,
  type,
  onChange,
  fixHint,
}: {
  value: string;
  type: string;
  onChange: (url: string) => void;
  fixHint?: string;
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const url = await uploadMenuItemImage(file, type || "custom_item");
      onChange(url);
    } catch (err: any) {
      setUploadError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const urlWarning = getImageUrlWarning(value, fixHint);

  return (
    <div>
      <label className={labelClass}>Image</label>
      <div className="flex items-center gap-3">
        <label
          className={`px-4 py-2 rounded font-semibold transition-colors ${
            uploading
              ? "bg-base-300 text-base-content/50 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer"
          }`}
        >
          {uploading ? "Uploading…" : "Upload image"}
          <input
            type="file"
            accept={ALLOWED_UPLOAD_TYPES.join(",")}
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        {value && (
          <img
            src={value}
            alt=""
            className="h-12 w-12 object-cover rounded border border-base-300"
          />
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} mt-2`}
        placeholder="or paste an image url - https://i.imgur.com/example.gif"
      />
      <p className="text-xs text-base-content/50 mt-1">
        Shown on the tip menu and in the Discord message. Gif, png, jpeg or
        webp, 5MB max: Discord fetches the image itself and often won't show
        anything bigger.
      </p>
      {uploadError ? (
        <p className="text-xs text-error mt-1">⚠️ {uploadError}</p>
      ) : (
        urlWarning && (
          <p className="text-xs text-warning mt-1">⚠️ {urlWarning}</p>
        )
      )}
    </div>
  );
};

const CustomTipMenuPage = () => {
  const { loading, error, data } = useQuery(GET_CUSTOM_TIP_MENU);
  const [updateMenuItem, { loading: updating }] = useMutation(
    UPDATE_CUSTOM_TIP_MENU_ITEM,
  );
  const [createMenuItem, { loading: creating }] = useMutation(
    CREATE_CUSTOM_TIP_MENU_ITEM,
  );
  const [previewMenuItem, { loading: previewing }] = useMutation(
    PREVIEW_CUSTOM_TIP_MENU_ITEM,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [sandboxDeploy, { loading: deploying }] =
    useMutation(SANDBOX_DEPLOY_MENU);
  const [editingItem, setEditingItem] = useState<CustomTipMenuItem | null>(
    null,
  );
  const [formData, setFormData] = useState<CustomTipMenuItem | null>(null);

  // Fetch protected images using custom hook
  const imageUrls = useProtectedMenuImages(
    data?.getBroadcasterCustomTipMenu?.menuItems,
    loading,
  );

  const handleEditClick = (item: CustomTipMenuItem) => {
    setEditingItem(item);
    setFormData({ ...item });
  };

  const handleCloseModal = () => {
    setEditingItem(null);
    setFormData(null);
  };

  const handleInputChange = (
    field: keyof CustomTipMenuItem,
    value: string | number | boolean | string[] | null,
  ) => {
    if (formData) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleTagToggle = (tag: string) => {
    if (!formData) return;
    const currentTags = formData.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];
    setFormData({ ...formData, tags: newTags });
  };

  const handleSave = async () => {
    if (!formData || !editingItem) return;

    // Normalizes legacy data (an item that ended up subscribable before the
    // toggle was disabled for its type) instead of sending a value the
    // server will now reject outright.
    const isSubscribable =
      formData.isSubscribable && isSubscribableType(formData.type);

    // Checked here too (the backend already rejects a non-positive price),
    // because `value ? Number(value) : null` below would otherwise treat an
    // explicit "0" the exact same way as an empty field - silently turning
    // the cadence off with no error, instead of surfacing "must be a
    // positive number" the way a negative value already does.
    if (isSubscribable) {
      for (const [field, label] of [
        ["subscriptionWeeklyPrice", "Weekly price"],
        ["subscriptionMonthlyPrice", "Monthly price"],
      ] as const) {
        const value = formData[field];
        if (value != null && value <= 0) {
          toast.error(`${label} must be a positive number`);
          return;
        }
      }
    }

    try {
      await updateMenuItem({
        variables: {
          itemName: editingItem.name,
          input: {
            name: formData.name,
            description: formData.description,
            cost: Number(formData.cost),
            friendlyName: formData.friendlyName,
            type: formData.type,
            command: formData.command,
            title: formData.title,
            footer: formData.footer,
            image: formData.image,
            tipNote: formData.tipNote,
            color: formData.color,
            isActive: formData.isActive,
            isPrivate: formData.isPrivate,
            isSubscribable,
            subscriptionWeeklyPrice: isSubscribable && formData.subscriptionWeeklyPrice != null
              ? Number(formData.subscriptionWeeklyPrice)
              : null,
            subscriptionMonthlyPrice: isSubscribable && formData.subscriptionMonthlyPrice != null
              ? Number(formData.subscriptionMonthlyPrice)
              : null,
            tags: formData.tags || [],
          },
        },
        refetchQueries: [{ query: GET_CUSTOM_TIP_MENU }],
      });
      toast.success("Menu item updated successfully!");
      handleCloseModal();
    } catch (err: any) {
      toast.error(err.message || "Failed to update menu item");
    }
  };

  // Sends whatever's in the form, saved or not, so an edit can be checked
  // before it goes live.
  const handlePreview = async (item: {
    name: string;
    type: string;
    cost: number | string;
    friendlyName: string;
    title: string;
    footer: string;
    image: string;
    color: string;
    isPrivate: boolean;
  }) => {
    try {
      await previewMenuItem({
        variables: {
          input: {
            name: item.name,
            type: item.type,
            cost: Number(item.cost) || 0,
            friendlyName: item.friendlyName,
            title: item.title,
            footer: item.footer,
            image: item.image,
            color: item.color,
            isPrivate: item.isPrivate,
          },
        },
      });
      toast.success("Preview sent to your Discord DMs");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleSandboxDeploy = async () => {
    if (
      !window.confirm(
        "⚠️ Deploy Rein's menu to CrimsonKitten?\n\nThis will REPLACE all of CrimsonKitten's menu items with Rein's current menu.\n\nAre you sure?",
      )
    ) {
      return;
    }

    try {
      await sandboxDeploy({
        variables: {
          sourceBroadcaster: "Rein",
          targetBroadcaster: "CrimsonKitten",
        },
      });
      toast.success("🚀 Menu deployed successfully to CrimsonKitten!");
    } catch (err: any) {
      toast.error(err.message || "Failed to deploy menu");
    }
  };

  const updateCreateForm = <K extends keyof typeof EMPTY_CREATE_FORM>(
    field: K,
    value: (typeof EMPTY_CREATE_FORM)[K],
  ) => setCreateForm((prev) => ({ ...prev, [field]: value }));

  const toggleCreateTag = (tag: string) =>
    setCreateForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));

  const closeCreateModal = () => {
    setIsCreating(false);
    setCreateForm(EMPTY_CREATE_FORM);
  };

  const handleCreate = async () => {
    const cost = Number(createForm.cost);
    if (!createForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!Number.isInteger(cost) || cost <= 0) {
      toast.error("Cost must be a positive whole number");
      return;
    }

    try {
      await createMenuItem({
        variables: {
          input: {
            name: createForm.name.trim(),
            friendlyName: createForm.friendlyName,
            cost,
            description: createForm.description,
            title: createForm.title,
            footer: createForm.footer,
            tipNote: createForm.tipNote,
            image: createForm.image,
            color: createForm.color,
            isPrivate: createForm.isPrivate,
            tags: createForm.tags,
          },
        },
        refetchQueries: [{ query: GET_CUSTOM_TIP_MENU }],
        awaitRefetchQueries: true,
      });
      toast.success("Menu item created!");
      closeCreateModal();
    } catch (err: any) {
      toast.error(err.message || "Failed to create menu item");
    }
  };

  if (loading) return <Loading />;
  if (error) return <div className="text-red-500">Error: {error.message}</div>;

  const customMenu: CustomTipMenu = data?.getBroadcasterCustomTipMenu;

  // Previewed so the broadcaster sees a clash before saving - the server
  // makes the same checks and has the final say.
  const createType = slugifyMenuItemType(createForm.name);
  const createCommand = slashCommandForType(createType);
  const getCreateNameProblem = () => {
    const name = createForm.name.trim();
    if (!name) return null;
    if (!createType) return "Needs at least one letter or number.";
    if (createType.length > MAX_MENU_ITEM_TYPE_LENGTH) {
      return "Name is too long.";
    }
    if (RESERVED_MENU_ITEM_TYPES.includes(createType)) {
      return `"${name}" is reserved for a built-in item - pick a different name.`;
    }
    if (
      customMenu?.menuItems?.some(
        (item) =>
          item.type === createType ||
          item.name === name ||
          (item.command ?? "").trim().replace(/^\//, "") ===
            createCommand.slice(1),
      )
    ) {
      return "An item with this name is already on your menu.";
    }
    return null;
  };
  const createNameProblem = getCreateNameProblem();

  const addItemButton = (
    <button
      onClick={() => setIsCreating(true)}
      className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
    >
      <span>＋</span>
      <span>Add Menu Item</span>
    </button>
  );

  const createModal = isCreating && (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-base-300">
        <div className="sticky top-0 bg-base-100 border-b border-base-300 p-6 z-10">
          <h2 className="text-2xl font-bold text-base-content">
            Add Menu Item
          </h2>
          <p className="text-base-content/70 text-sm mt-1">
            Tippers can redeem it from the tip menu on the site, or with its
            own Discord slash command.
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className={labelClass}>Name *</label>
            <input
              type="text"
              value={createForm.name}
              onChange={(e) => updateCreateForm("name", e.target.value)}
              className={inputClass}
              placeholder="e.g. Foot Rub"
              maxLength={60}
            />
            {createNameProblem ? (
              <p className="text-xs text-warning mt-1">
                ⚠️ {createNameProblem}
              </p>
            ) : (
              createType && (
                <p className="text-xs text-base-content/50 mt-1">
                  Item ID: <code>{createType}</code> · Discord command:{" "}
                  <code>{createCommand}</code>
                </p>
              )
            )}
          </div>

          <div>
            <label className={labelClass}>Display Name</label>
            <input
              type="text"
              value={createForm.friendlyName}
              onChange={(e) => updateCreateForm("friendlyName", e.target.value)}
              className={inputClass}
              placeholder="Shown to tippers - defaults to the name"
            />
          </div>

          <div>
            <label className={labelClass}>Cost (Crimcoins) *</label>
            <input
              type="number"
              min="1"
              step="1"
              value={createForm.cost}
              onChange={(e) => updateCreateForm("cost", e.target.value)}
              className={inputClass}
              placeholder="e.g. 100"
            />
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={createForm.description}
              onChange={(e) => updateCreateForm("description", e.target.value)}
              className={`${inputClass} h-24`}
              placeholder="What the tipper gets"
            />
          </div>

          <div>
            <label className={labelClass}>Discord Message Title</label>
            <input
              type="text"
              value={createForm.title}
              onChange={(e) => updateCreateForm("title", e.target.value)}
              className={inputClass}
              placeholder='Defaults to "Custom Tip"'
            />
          </div>

          <div>
            <label className={labelClass}>Discord Message Footer</label>
            <input
              type="text"
              value={createForm.footer}
              onChange={(e) => updateCreateForm("footer", e.target.value)}
              className={inputClass}
              placeholder="Use {username} for the tipper's name"
            />
          </div>

          <div>
            <label className={labelClass}>Tip History Note</label>
            <input
              type="text"
              value={createForm.tipNote}
              onChange={(e) => updateCreateForm("tipNote", e.target.value)}
              className={inputClass}
              placeholder='Defaults to "Redeemed <display name>"'
            />
          </div>

          <ImageField
            value={createForm.image}
            type={createType}
            onChange={(url) => updateCreateForm("image", url)}
            fixHint={CREATE_IMAGE_HINT}
          />

          <div>
            <label className={labelClass}>Color</label>
            <select
              value={createForm.color}
              onChange={(e) => updateCreateForm("color", e.target.value)}
              className={inputClass}
            >
              <option value="">Default (Gray)</option>
              <option value="Red">Red</option>
              <option value="Green">Green</option>
              <option value="Purple">Purple</option>
              <option value="LuminousVividPink">Pink</option>
              <option value="Blue">Blue</option>
            </select>
          </div>

          <div className="flex items-center justify-between bg-base-200 border border-base-300 rounded px-4 py-3">
            <div>
              <label className="block text-sm font-semibold text-base-content">
                🔒 Private
              </label>
              <p className="text-xs text-base-content/50 mt-1">
                DM the redemption to the tipper and to you instead of posting
                in the server
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                updateCreateForm("isPrivate", !createForm.isPrivate)
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                createForm.isPrivate ? "bg-blue-600" : "bg-base-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  createForm.isPrivate ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div className="bg-base-200 border border-base-300 rounded px-4 py-3">
            <label className="block text-sm font-semibold text-base-content mb-3">
              Tags (Select all that apply)
            </label>
            <div className="space-y-2">
              {Object.entries(TAG_CONFIG).map(
                ([key, { icon, label, description }]) => (
                  <label
                    key={key}
                    className="flex items-start gap-3 p-2 rounded hover:bg-base-300 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={createForm.tags.includes(key)}
                      onChange={() => toggleCreateTag(key)}
                      className="mt-1 w-4 h-4 text-purple-600 bg-base-200 border-base-300 rounded focus:ring-purple-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{icon}</span>
                        <span className="text-sm font-semibold text-base-content">
                          {label}
                        </span>
                      </div>
                      <p className="text-xs text-base-content/60 mt-1">
                        {description}
                      </p>
                    </div>
                  </label>
                ),
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-base-100 border-t border-base-300 p-6 flex flex-wrap gap-3 justify-end">
          <button
            onClick={() =>
              handlePreview({ ...createForm, type: createType })
            }
            disabled={creating || previewing || !createForm.name.trim()}
            title="DMs you how this item looks when redeemed"
            className="mr-auto px-6 py-2 border border-purple-600 text-purple-400 hover:bg-purple-600/10 rounded font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {previewing ? "Sending..." : "Send Preview"}
          </button>
          <button
            onClick={closeCreateModal}
            disabled={creating}
            className="px-6 py-2 bg-base-300 hover:bg-base-300/80 text-base-content rounded font-semibold transition-colors duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={
              creating ||
              !createType ||
              !!createNameProblem ||
              !createForm.cost
            }
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? "Creating..." : "Create Item"}
          </button>
        </div>
      </div>
    </div>
  );

  if (
    !customMenu ||
    !customMenu.menuItems ||
    customMenu.menuItems.length === 0
  ) {
    return (
      <div className="min-h-screen bg-base-200 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold text-base-content mb-2">
            Offline Tip Menu
          </h1>
          <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-6 mt-8">
            <p className="text-yellow-100 text-lg">
              Your tip menu is empty - add your first item to get started.
            </p>
          </div>
          <div className="mt-4">{addItemButton}</div>
        </div>
        {createModal}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-base-content mb-2">
            Offline Tip Menu
          </h1>
          <p className="text-base-content/70">
            What tippers redeem with crimcoins on Reinstorm and through the
            Discord bot, between shows. For what a Chaturbate tip buys on
            stream, use the CB Tip Menu.
          </p>
          <div className="mt-4 bg-purple-900 border border-purple-700 rounded-lg p-4">
            <p className="text-purple-100 text-sm">
              <span className="font-semibold">💡 Total Items:</span>{" "}
              {customMenu.menuItems.length} tip options available
            </p>
          </div>

          <div className="mt-4">{addItemButton}</div>

          {/* Sandbox Deploy Button (Rein only) */}
          {customMenu.broadcaster.toLowerCase() === "rein" && (
            <div className="mt-4">
              <button
                onClick={handleSandboxDeploy}
                disabled={deploying}
                className="w-full md:w-auto bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
              >
                {deploying ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>Deploying...</span>
                  </>
                ) : (
                  <>
                    <span>🚀</span>
                    <span>Deploy to CrimsonKitten (Production)</span>
                  </>
                )}
              </button>
              <p className="text-xs text-base-content/60 mt-2">
                ⚠️ Developer Tool: This will replace CrimsonKitten's entire menu
                with Rein's current menu
              </p>
            </div>
          )}
        </div>

        {/* Menu Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customMenu.menuItems.map((item, index) => (
            <div
              key={`${item.name}-${index}`}
              className="bg-base-100 rounded-lg border border-base-300 overflow-hidden hover:border-purple-500 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
            >
              {/* Image/Icon Section - falls back to the gradient tile when
                  the item has no loadable image */}
              {imageUrls[item.type] ? (
                <div className="h-48 bg-base-300 overflow-hidden relative">
                  <img
                    src={imageUrls[item.type]}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div
                  className="h-48 flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${
                      item.color === "Red"
                        ? "#dc2626, #991b1b"
                        : item.color === "Green"
                          ? "#16a34a, #15803d"
                          : item.color === "Purple"
                            ? "#9333ea, #7e22ce"
                            : item.color === "LuminousVividPink"
                              ? "#ec4899, #db2777"
                              : item.color === "Blue"
                                ? "#2563eb, #1d4ed8"
                                : "#6b7280, #4b5563"
                    })`,
                  }}
                >
                  <div className="text-6xl">
                    {item.type === "deny"
                      ? "❌"
                      : item.type === "undeny"
                        ? "✅"
                        : item.type === "edge"
                          ? "💦"
                          : item.type === "headpats"
                            ? "💜"
                            : item.type === "spanks"
                              ? "👋"
                              : item.type === "hydrate"
                                ? "💧"
                                : "✨"}
                  </div>
                </div>
              )}

              {/* Content Section */}
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-base-content mb-1">
                      {item.friendlyName || item.name}
                    </h3>
                    <p className="text-sm text-base-content/60 uppercase tracking-wide">
                      {item.type}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-400">
                      {item.cost}
                    </div>
                    <div className="text-xs text-base-content/50">
                      Crimcoins
                    </div>
                  </div>
                </div>

                {/* Description */}
                {item.description && (
                  <p className="text-base-content/80 text-sm mb-4 line-clamp-2">
                    {item.description}
                  </p>
                )}

                {/* Details */}
                <div className="space-y-2">
                  {item.command && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-base-content/50">
                        Command:
                      </span>
                      <code className="text-xs bg-base-300 text-purple-500 px-2 py-1 rounded">
                        {item.command}
                      </code>
                    </div>
                  )}
                  {item.title && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-base-content/50">
                        Title:
                      </span>
                      <span className="text-xs text-base-content/80">
                        {item.title}
                      </span>
                    </div>
                  )}
                  {item.tipNote && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-base-content/50">
                        Note:
                      </span>
                      <span className="text-xs text-base-content/80">
                        {item.tipNote}
                      </span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {item.footer && (
                  <div className="mt-4 pt-4 border-t border-base-300">
                    <p className="text-xs text-base-content/60 italic">
                      {item.footer}
                    </p>
                  </div>
                )}

                {/* Tags Display */}
                {item.tags && item.tags.length > 0 && (
                  <div className="mb-4 pt-4 border-t border-base-300">
                    <p className="text-xs text-base-content/50 mb-2">Tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag) => {
                        const tagKey = tag as keyof typeof TAG_CONFIG;
                        const tagInfo = TAG_CONFIG[tagKey];
                        if (!tagInfo) return null;

                        return (
                          <span
                            key={tag}
                            title={tagInfo.description}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-purple-900 border border-purple-700 rounded-full text-xs text-purple-200"
                          >
                            <span>{tagInfo.icon}</span>
                            <span>{tagInfo.label}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Edit Button */}
                <button
                  onClick={() => handleEditClick(item)}
                  className="mt-4 w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded transition-colors duration-200"
                >
                  Edit Item
                </button>

                {/* Active Status Badge */}
                <div className="mt-2 text-center">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      item.isActive
                        ? "bg-green-900 text-green-300 border border-green-700"
                        : "bg-red-900 text-red-300 border border-red-700"
                    }`}
                  >
                    {item.isActive ? "✓ Active" : "✗ Inactive"}
                  </span>
                  {item.isPrivate && (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-900 text-blue-300 border border-blue-700">
                      🔒 Private
                    </span>
                  )}
                  {item.isSubscribable && (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-indigo-900 text-indigo-300 border border-indigo-700">
                      🔁 Subscribable
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Edit Modal */}
        {editingItem && formData && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-base-100 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-base-300">
              {/* Modal Header */}
              <div className="sticky top-0 bg-base-100 border-b border-base-300 p-6 z-10">
                <h2 className="text-2xl font-bold text-base-content">
                  Edit Menu Item
                </h2>
                <p className="text-base-content/70 text-sm mt-1">
                  Update the details for "
                  {editingItem.friendlyName || editingItem.name}"
                </p>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-4">
                {/* Friendly Name */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Friendly Name *
                  </label>
                  <input
                    type="text"
                    value={formData.friendlyName}
                    onChange={(e) =>
                      handleInputChange("friendlyName", e.target.value)
                    }
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                    placeholder="Display name for this item"
                  />
                </div>

                {/* Cost */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Cost (Crimcoins) *
                  </label>
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) =>
                      handleInputChange("cost", Number(e.target.value))
                    }
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                    min="0"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500 h-24"
                    placeholder="Description of this menu item"
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Type
                  </label>
                  <input
                    type="text"
                    value={formData.type}
                    onChange={(e) => handleInputChange("type", e.target.value)}
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                    placeholder="e.g., deny, undeny, edge, spanks"
                  />
                </div>

                {/* Command */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Command
                  </label>
                  <input
                    type="text"
                    value={formData.command}
                    onChange={(e) =>
                      handleInputChange("command", e.target.value)
                    }
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                    placeholder="Bot command"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                    placeholder="Title text"
                  />
                </div>

                {/* Footer */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Footer
                  </label>
                  <input
                    type="text"
                    value={formData.footer}
                    onChange={(e) =>
                      handleInputChange("footer", e.target.value)
                    }
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                    placeholder="Footer text"
                  />
                </div>

                <ImageField
                  value={formData.image}
                  type={formData.type}
                  onChange={(url) => handleInputChange("image", url)}
                />

                {/* Tip Note */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Tip Note
                  </label>
                  <input
                    type="text"
                    value={formData.tipNote}
                    onChange={(e) =>
                      handleInputChange("tipNote", e.target.value)
                    }
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                    placeholder="Note displayed with tip"
                  />
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-semibold text-base-content mb-2">
                    Color
                  </label>
                  <select
                    value={formData.color}
                    onChange={(e) => handleInputChange("color", e.target.value)}
                    className="w-full bg-base-200 border border-base-300 rounded px-4 py-2 text-base-content focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Default (Gray)</option>
                    <option value="Red">Red</option>
                    <option value="Green">Green</option>
                    <option value="Purple">Purple</option>
                    <option value="LuminousVividPink">Pink</option>
                    <option value="Blue">Blue</option>
                  </select>
                </div>

                {/* Is Active Toggle */}
                <div className="flex items-center justify-between bg-base-200 border border-base-300 rounded px-4 py-3">
                  <div>
                    <label className="block text-sm font-semibold text-base-content">
                      Active Status
                    </label>
                    <p className="text-xs text-base-content/50 mt-1">
                      Inactive items won't be visible to tippers
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange("isActive", !formData.isActive)
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.isActive ? "bg-green-600" : "bg-base-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.isActive ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Is Private Toggle */}
                <div className="flex items-center justify-between bg-base-200 border border-base-300 rounded px-4 py-3">
                  <div>
                    <label className="block text-sm font-semibold text-base-content">
                      🔒 Private
                    </label>
                    <p className="text-xs text-base-content/50 mt-1">
                      DM the redemption to the tipper and to you instead of
                      posting in the server
                    </p>
                    {formData.type && !supportsPrivateRedeem(formData.type) && (
                      <p className="text-xs text-warning mt-1">
                        ⚠️ "{formData.type}" redeems through its own handler and
                        always posts in the server — private has no effect on it
                        yet
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      handleInputChange("isPrivate", !formData.isPrivate)
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.isPrivate ? "bg-blue-600" : "bg-base-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.isPrivate ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Is Subscribable Toggle */}
                <div className="bg-base-200 border border-base-300 rounded px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-semibold text-base-content">
                        🔁 Subscribable
                      </label>
                      <p className="text-xs text-base-content/50 mt-1">
                        Let tippers set up a recurring weekly/monthly
                        auto-redeem of this item at the price(s) below
                      </p>
                      {formData.type && !isSubscribableType(formData.type) && (
                        <p className="text-xs text-warning mt-1">
                          ⚠️ "{formData.type}" redeems through its own handler
                          and can't be made subscribable
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={!isSubscribableType(formData.type)}
                      onClick={() =>
                        handleInputChange(
                          "isSubscribable",
                          !formData.isSubscribable
                        )
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                        formData.isSubscribable
                          ? "bg-indigo-600"
                          : "bg-base-300"
                      } ${!isSubscribableType(formData.type) ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.isSubscribable
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {formData.isSubscribable && isSubscribableType(formData.type) && (
                    <>
                    <p className="text-xs text-base-content/60 mt-3">
                      Subscribing charges whichever is higher of the regular
                      price and the renewal price below; every renewal after
                      that charges the renewal price. So a discount goes to
                      people who stay subscribed.
                      {[
                        ["week", formData.subscriptionWeeklyPrice],
                        ["month", formData.subscriptionMonthlyPrice],
                      ]
                        .filter(([, renewal]) => Number(renewal) > 0)
                        .map(([per, renewal]) => {
                          const first = Math.max(
                            Number(formData.cost) || 0,
                            Number(renewal),
                          );
                          return (
                            <span key={String(per)} className="block">
                              Per {per}: {first} CC first, then {Number(renewal)}{" "}
                              CC.
                            </span>
                          );
                        })}
                    </p>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-xs text-base-content/60 mb-1">
                          Weekly renewal price (leave blank to disable weekly)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.subscriptionWeeklyPrice ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              "subscriptionWeeklyPrice",
                              e.target.value === ""
                                ? null
                                : Number(e.target.value)
                            )
                          }
                          className="w-full bg-base-100 border border-base-300 rounded px-3 py-2 text-base-content focus:outline-none focus:border-indigo-500"
                          placeholder={`e.g. ${Math.round(
                            (formData.cost || 0) * 0.9
                          )}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-base-content/60 mb-1">
                          Monthly renewal price (leave blank to disable monthly)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.subscriptionMonthlyPrice ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              "subscriptionMonthlyPrice",
                              e.target.value === ""
                                ? null
                                : Number(e.target.value)
                            )
                          }
                          className="w-full bg-base-100 border border-base-300 rounded px-3 py-2 text-base-content focus:outline-none focus:border-indigo-500"
                          placeholder={`e.g. ${Math.round(
                            (formData.cost || 0) * 0.85
                          )}`}
                        />
                      </div>
                    </div>
                    </>
                  )}
                </div>

                {/* Tags Selection */}
                <div className="bg-base-200 border border-base-300 rounded px-4 py-3">
                  <label className="block text-sm font-semibold text-base-content mb-3">
                    Tags (Select all that apply)
                  </label>
                  <div className="space-y-2">
                    {Object.entries(TAG_CONFIG).map(
                      ([key, { icon, label, description }]) => (
                        <label
                          key={key}
                          className="flex items-start gap-3 p-2 rounded hover:bg-base-300 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.tags?.includes(key) || false}
                            onChange={() => handleTagToggle(key)}
                            className="mt-1 w-4 h-4 text-purple-600 bg-base-200 border-base-300 rounded focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{icon}</span>
                              <span className="text-sm font-semibold text-base-content">
                                {label}
                              </span>
                            </div>
                            <p className="text-xs text-base-content/60 mt-1">
                              {description}
                            </p>
                          </div>
                        </label>
                      ),
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-base-100 border-t border-base-300 p-6 flex flex-wrap gap-3 justify-end">
                <button
                  onClick={() => handlePreview(formData)}
                  disabled={updating || previewing}
                  title="DMs you how this item looks when redeemed"
                  className="mr-auto px-6 py-2 border border-purple-600 text-purple-400 hover:bg-purple-600/10 rounded font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {previewing ? "Sending..." : "Send Preview"}
                </button>
                <button
                  onClick={handleCloseModal}
                  disabled={updating}
                  className="px-6 py-2 bg-base-300 hover:bg-base-300/80 text-base-content rounded font-semibold transition-colors duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updating}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {createModal}
      </div>
    </div>
  );
};

export default CustomTipMenuPage;
